import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowRight, ArrowUpRight, Calendar, ChevronLeft, ChevronRight, Search, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBlog } from '@/context/BlogContext';
import { BlogPost } from '@/types/types';
import { Helmet } from 'react-helmet';

// Blog post card skeleton
const BlogPostSkeleton = () => (
  <Card className="overflow-hidden">
    <div className="aspect-video relative overflow-hidden">
      <Skeleton className="w-full h-full" />
    </div>
    <CardHeader className="pb-2">
      <Skeleton className="h-4 w-1/3 mb-2" />
      <Skeleton className="h-6 w-full" />
    </CardHeader>
    <CardContent className="pb-2">
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-5/6" />
    </CardContent>
    <CardFooter className="pt-0 flex gap-2">
      <Skeleton className="h-6 w-16" />
      <Skeleton className="h-6 w-16" />
    </CardFooter>
  </Card>
);

// Recent post large skeleton
const RecentPostLargeSkeleton = () => (
  <Card className="overflow-hidden h-full">
    <div className="aspect-video relative overflow-hidden">
      <Skeleton className="w-full h-full" />
    </div>
    <CardHeader className="pb-2">
      <Skeleton className="h-4 w-1/3 mb-2" />
      <Skeleton className="h-7 w-full" />
    </CardHeader>
    <CardContent className="pb-2">
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-5/6 mb-2" />
      <Skeleton className="h-4 w-4/6" />
    </CardContent>
    <CardFooter className="pt-0 flex gap-2">
      <Skeleton className="h-6 w-16" />
      <Skeleton className="h-6 w-16" />
    </CardFooter>
  </Card>
);

// Recent post small skeleton
const RecentPostSmallSkeleton = () => (
  <Card className="overflow-hidden">
    <div className="flex flex-col md:flex-row gap-4">
      <div className="md:w-1/3 aspect-video relative overflow-hidden">
        <Skeleton className="w-full h-full" />
      </div>
      <div className="md:w-2/3 p-4">
        <Skeleton className="h-4 w-1/3 mb-2" />
        <Skeleton className="h-6 w-full mb-3" />
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-5/6 mb-4" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-6 w-16" />
        </div>
      </div>
    </div>
  </Card>
);


// Blog post card component
const BlogPostCard = ({ post }: { post: BlogPost }) => {
  const navigate = useNavigate();
  
  const handlePostClick = () => {
    navigate(`/blog/${post.slug}`);
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow duration-300">
      <div className="relative">
        <div className="aspect-video relative overflow-hidden">
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </div>
        <Button 
          size="icon" 
          variant="ghost" 
          className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full"
          onClick={handlePostClick}
        >
          <ArrowUpRight className="h-4 w-4" />
        </Button>
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <span>{post.author.name}</span>
          <span>•</span>
          <span>{post.date}</span>
        </div>
        <CardTitle 
          className="text-xl hover:text-blue-600 cursor-pointer transition-colors duration-300"
          onClick={handlePostClick}
        >
          {post.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        <CardDescription className="line-clamp-2">
          {post.description}
        </CardDescription>
      </CardContent>
      <CardFooter className="pt-0 flex flex-wrap gap-2">
        {post.tags.map((tag, idx) => (
          <Badge key={idx} variant="outline" className="hover:bg-gray-100 cursor-pointer">
            {tag}
          </Badge>
        ))}
      </CardFooter>
    </Card>
  );
};



// Featured post component (larger display)
const FeaturedPost = ({ post }: { post: BlogPost }) => {
  const navigate = useNavigate();
  
  const handlePostClick = () => {
    navigate(`/blog/${post.slug}`);
  };

  return (
    <Card className="overflow-hidden h-full hover:shadow-md transition-shadow duration-300">
      <div className="relative">
        <div className="aspect-video md:aspect-[16/10] relative overflow-hidden">
          <img 
            src={post.image} 
            alt={post.title} 
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </div>
        <Button 
          size="icon" 
          variant="ghost" 
          className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full"
          onClick={handlePostClick}
        >
          <ArrowUpRight className="h-4 w-4" />
        </Button>
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <span>{post.author.name}</span>
          <span>•</span>
          <span>{post.date}</span>
        </div>
        <CardTitle 
          className="text-2xl hover:text-blue-600 cursor-pointer transition-colors duration-300"
          onClick={handlePostClick}
        >
          {post.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-2">
        <CardDescription className="line-clamp-3">
          {post.description}
        </CardDescription>
      </CardContent>
      <CardFooter className="pt-0 flex flex-wrap gap-2">
        {post.tags.map((tag, idx) => (
          <Badge key={idx} variant="outline" className="hover:bg-gray-100 cursor-pointer">
            {tag}
          </Badge>
        ))}
      </CardFooter>
    </Card>
  );
};

// Recent post small component
const RecentPostSmall = ({ post }: { post: BlogPost }) => {
  const navigate = useNavigate();
  
  const handlePostClick = () => {
    navigate(`/blog/${post.slug}`);
  };

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow duration-300">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="md:w-1/3 relative">
          <div className="aspect-video relative overflow-hidden">
            <img 
              src={post.image} 
              alt={post.title} 
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            />
          </div>
          <Button 
            size="icon" 
            variant="ghost" 
            className="absolute top-2 right-2 bg-white/80 hover:bg-white rounded-full"
            onClick={handlePostClick}
          >
            <ArrowUpRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="md:w-2/3 p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <span>{post.author.name}</span>
            <span>•</span>
            <span>{post.date}</span>
          </div>
          <h3 
            className="text-xl font-bold mb-2 hover:text-blue-600 cursor-pointer transition-colors duration-300"
            onClick={handlePostClick}
          >
            {post.title}
          </h3>
          <p className="text-gray-600 mb-4 line-clamp-2">{post.description}</p>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="hover:bg-gray-100 cursor-pointer">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

// Pagination component
const Pagination = ({ currentPage, totalPages, paginate }: { currentPage: number, totalPages: number, paginate: (page: number) => void }) => {
  const pageNumbers = [];
  
  // Logic to display page numbers
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, currentPage + 2);
  
  // Ensure we always show 5 pages if possible
  if (endPage - startPage < 4) {
    if (startPage === 1) {
      endPage = Math.min(5, totalPages);
    } else if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - 4);
    }
  }
  
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }
  
  return (
    <div className="flex justify-center items-center mt-8 gap-2">
      <Button 
        variant="outline" 
        size="icon" 
        onClick={() => paginate(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      {startPage > 1 && (
        <>
          <Button variant={currentPage === 1 ? "default" : "outline"} size="sm" onClick={() => paginate(1)}>
            1
          </Button>
          {startPage > 2 && <span className="mx-1">...</span>}
        </>
      )}
      
      {pageNumbers.map(number => (
        <Button 
          key={number}
          variant={currentPage === number ? "default" : "outline"}
          size="sm"
          onClick={() => paginate(number)}
        >
          {number}
        </Button>
      ))}
      
      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span className="mx-1">...</span>}
          <Button variant="outline" size="sm" onClick={() => paginate(totalPages)}>
            {totalPages}
          </Button>
        </>
      )}
      
      <Button 
        variant="outline" 
        size="icon" 
        onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

const Blog = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useBlog();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;


   // Get posts from context
   const { posts, recentPosts, isLoading, error } = state;
  
   useEffect(() => {
     // Reset pagination when posts change
     setCurrentPage(1);
   }, [posts]);
 

  // Filter posts based on search query
  const filteredPosts = useMemo(() => {
    if (!searchQuery.trim()) return posts;
    
    return posts.filter(post => 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.author.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [posts, searchQuery]);



  // Get current posts for pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Change page
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  return (
    <>
    <Helmet>
    <title>Mopaossi | Blog</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
    <PageLayout
      title="Mopaossi | Blog"
      description="Tutos, Tools, AI, No-Code, Low-Code, on parle..."
    >
      <div className="space-y-12">
        {/* Search bar */}
        <div className="max-w-md mx-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Votre sujet..."
              className="pl-10 py-6 text-lg"
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
        </div>

        {/* Recent blog posts */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Nouveautés dans le Blog</h2>
          
          {isLoading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-1 space-y-6">
                <RecentPostSmallSkeleton />
                <RecentPostSmallSkeleton />
              </div>
              <div className="lg:col-span-1">
                <RecentPostLargeSkeleton />
              </div>
            </div>
          ) : error ? (
            <div className="text-red-500">Error loading blog posts: {error.message}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-1 space-y-6">
                {recentPosts.slice(1, 3).map((post, index) => (
                  <RecentPostSmall key={post.id} post={post} />
                ))}
              </div>
              <div className="lg:col-span-1">
                {recentPosts.length > 0 && (
                  <FeaturedPost post={recentPosts[0]} />
                )}
              </div>
            </div>
          )}
        </div>

        {/* All blog posts */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">Tous les articles</h2>
          
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => (
                <BlogPostSkeleton key={index} />
              ))}
            </div>
          ) : error ? (
            <div className="text-red-500">Error loading blog posts: {error.message}</div>
          ) : currentPosts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium mb-2">Aucun résultat...</h3>
              <p className="text-gray-500 mb-4">Veuillez ajuster le critère de vore recherche</p>
              <Button  onClick={() => setSearchQuery('')}>Effacer</Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentPosts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage} 
                  totalPages={totalPages} 
                  paginate={paginate} 
                />
              )}
            </>
          )}
        </div>
      </div>
    </PageLayout>
    </>

  );
};

export default Blog;
